// components/invoice/UnrequestedCompletedWorksList.tsx

import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, Button, Typography, Box, Collapse, 
  IconButton, Chip, CircularProgress, Alert, useTheme, useMediaQuery
} from '@mui/material';
import { 
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { invoiceRequestService } from '../../services/invoiceRequest';
import { useUser } from '../../hooks/useUser';
import { CompletedWorkWithoutInvoice } from '../../types/invoiceRequest';
import { InvoiceRequestDialog } from '../InvoiceRequest/InvoiceRequestDialog'; // 必要に応じて実装

export const UnrequestedCompletedWorksList: React.FC = () => {
  const [completedWorks, setCompletedWorks] = useState<CompletedWorkWithoutInvoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<{ [key: string]: boolean }>({});
  const [open, setOpen] = useState(false); // 折りたたみ状態管理、デフォルトで閉じる
  
  // 請求書作成依頼ダイアログ用の状態変数
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState<boolean>(false);
  const [selectedWorkId, setSelectedWorkId] = useState<number | null>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // ユーザー情報を取得
  const { userInfo, isLoading: isUserLoading } = useUser();

  // 日付をフォーマットする関数
  const formatDate = (dateString: string) => {
    if (!dateString) return '日付なし';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '年').replace(/\//g, '月') + '日';
    } catch (error) {
      return '無効な日付';
    }
  };

  // 金額をフォーマットする関数
  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return '¥0';
    return new Intl.NumberFormat('ja-JP', { 
      style: 'currency', 
      currency: 'JPY' 
    }).format(amount);
  };

  // データ取得
  const fetchData = async () => {
    if (!open) return; // 閉じている間はデータを取得しない
    
    setLoading(true);
    setError(null);
    try {
      const response = await invoiceRequestService.getAllCompletedWorksWithoutInvoice();
      if (response.success) {
        setCompletedWorks(response.data);
      } else {
        setError('データの取得に失敗しました');
      }
    } catch (err) {
      setError('エラーが発生しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]); // open状態が変わった時にのみデータを取得

  // 請求書依頼作成ボタンのハンドラ
  const handleCreateInvoiceRequest = (id: number) => {
    setSelectedWorkId(id);
    setInvoiceDialogOpen(true);
  };

  // 従来のボタンクリック処理（ダイアログを使用しない場合）
  const handleCreateInvoiceRequestDirect = async (workId: string) => {
    if (!userInfo || !userInfo.ID) {
      setError('ユーザー情報が取得できません');
      return;
    }
    
    // 処理中のIDを記録
    setProcessing(prev => ({ ...prev, [workId]: true }));
    
    try {
      const userId = userInfo.ID.toString();
      await invoiceRequestService.createInvoiceRequestForConstruction(workId, userId);
      // 成功したら一覧から削除
      setCompletedWorks(prev => prev.filter(work => work.ID.toString() !== workId));
    } catch (err) {
      console.error('請求書依頼作成エラー:', err);
      setError('請求書依頼の作成に失敗しました');
    } finally {
      // 処理中のIDを処理完了
      setProcessing(prev => {
        const newState = { ...prev };
        delete newState[workId];
        return newState;
      });
    }
  };

  // 請求書作成依頼成功時のコールバック
  const handleInvoiceRequestSuccess = () => {
    if (selectedWorkId) {
      // 成功したら一覧から削除
      setCompletedWorks(prev => prev.filter(work => work.ID !== selectedWorkId));
    }
    setInvoiceDialogOpen(false);
  };

  // モバイル用の表示
  const renderMobileView = () => (
    <Box sx={{ width: '100%' }}>
      {completedWorks.map((work) => (
        <Paper
          key={`card-${work.ID}`}
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid #e0e0e0',
            borderLeft: '4px solid #3f51b5'
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {work.customerName} - {work.siteName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {work.workContent || '(工事内容なし)'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
            <Typography variant="body2">担当者:</Typography>
            <Typography variant="body2">{work.supervisor || '-'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">完了日:</Typography>
            <Typography variant="body2">{formatDate(work.lastUpdatedDate_str || '')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">見積金額:</Typography>
            <Typography variant="body2" fontWeight="bold">{formatCurrency(work.estimatedAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!!processing[work.ID.toString()]}
              onClick={() => handleCreateInvoiceRequest(work.ID)}
              sx={{ minWidth: '120px' }}
            >
              {processing[work.ID.toString()] ? 
                <CircularProgress size={16} color="inherit" /> : 
                '請求書依頼作成'
              }
            </Button>
          </Box>
        </Paper>
      ))}
    </Box>
  );

  // デスクトップ用の表示
  const renderDesktopTable = () => (
    <TableContainer component={Paper} sx={{ 
      maxWidth: '100%', 
      overflowX: 'auto',
      // モバイル表示時の調整
      '@media (max-width:600px)': {
        '& .MuiTableCell-root': {
          padding: '8px 6px', // セル内の余白を減らす
          fontSize: '0.75rem', // フォントサイズを小さくする
        },
        '& .MuiButton-root': {
          padding: '4px 8px', // ボタンサイズを調整
          fontSize: '0.7rem',
        }
      }
    }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell>工事ID</TableCell>
            <TableCell>顧客名</TableCell>
            <TableCell>現場名</TableCell>
            <TableCell>工事内容</TableCell>
            <TableCell>担当者</TableCell>
            <TableCell>完了日</TableCell>
            <TableCell>見積金額</TableCell>
            <TableCell>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {completedWorks.map((work) => (
            <TableRow key={work.ID} hover>
              <TableCell>{work.ID}</TableCell>
              <TableCell>{work.customerName}</TableCell>
              <TableCell>{work.siteName}</TableCell>
              <TableCell>{work.workContent}</TableCell>
              <TableCell>{work.supervisor}</TableCell>
              <TableCell>{formatDate(work.lastUpdatedDate_str || '')}</TableCell>
              <TableCell>{formatCurrency(work.estimatedAmount)}</TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={!!processing[work.ID.toString()]}
                  onClick={() => handleCreateInvoiceRequest(work.ID)}
                >
                  {processing[work.ID.toString()] ? 
                    <CircularProgress size={16} color="inherit" /> : 
                    '請求書依頼作成'
                  }
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ 
      mb: 4, 
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      overflow: 'hidden'
    }}>
      {/* ヘッダー部分 - 常に表示 */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5', 
        borderBottom: open ? '1px solid #e0e0e0' : 'none'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            請求書依頼未作成の完了工事（全担当者）
          </Typography>
          {!open && completedWorks.length > 0 && (
            <Chip 
              color="primary" 
              label={`${completedWorks.length}件`} 
              size="small" 
              sx={{ mr: 1 }}
            />
          )}
        </Box>
        <IconButton
          onClick={() => setOpen(!open)}
          aria-label={open ? '折りたたむ' : '展開する'}
          size="small"
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {/* コンテンツ部分 - 折りたたみ可能 */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : completedWorks.length === 0 ? (
            <Typography>現在、請求書依頼未作成の完了工事はありません。</Typography>
          ) : (
            isMobile ? renderMobileView() : renderDesktopTable()
          )}
        </Box>
      </Collapse>

      {/* 請求書作成依頼ダイアログ - 必要に応じて実装 */}
      {invoiceDialogOpen && (
        <InvoiceRequestDialog
          open={invoiceDialogOpen}
          onClose={() => setInvoiceDialogOpen(false)}
          constructionId={selectedWorkId || 0}
          onSuccess={handleInvoiceRequestSuccess}
        />
      )}
    </Box>
  );
};

// export default UnrequestedCompletedWorksList;